import { Icon, useObsinaI18n } from "@obsina/design-system";
import type { Tone } from "@obsina/design-system";
import type { CoverageReason } from "@obsina/domain";
import type { LucideIcon } from "lucide-react";
import {
  Box,
  Building2,
  Calendar,
  CalendarClock,
  CalendarPlus,
  ClipboardList,
  Clock,
  CreditCard,
  File,
  FileSearch,
  FileSignature,
  FileText,
  Frame,
  Gavel,
  Handshake,
  Lock,
  Mail,
  Maximize,
  MessageSquareWarning,
  MessagesSquare,
  Minus,
  Paperclip,
  PenLine,
  Plus,
  Receipt,
  ReceiptText,
  Replace,
  Reply,
  Search,
  Truck,
  User,
  Users,
  X,
} from "lucide-react";
import {
  memo,
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type CSSProperties,
  type KeyboardEvent,
  type MouseEvent,
  type PointerEvent,
  type ReactNode,
  type WheelEvent,
} from "react";
import { ENTITY_RING_TOKEN, GRAPH_ZOOM, LAYOUT_REFERENCE } from "./types";
import type {
  ClaimState,
  EntityType,
  EvidenceChannel,
  EvidenceKind,
  GraphInteractionState,
  GraphNode,
  KnowledgeCoverage,
  LayoutEdge,
  LayoutResult,
  MemoryGraphData,
  MemoryGraphMessages,
  NodeRenderState,
  NodeStatusBadge,
  NormalizedPoint,
  UnknownMemoryGraph,
} from "./types";
import "./memory-graph.css";

/* ------------------------------------------------------------------------- *
 * Shared lucide component maps and formatting helpers (imported by the sibling
 * inspector and narrow-list components so the icon vocabulary lives once).
 * ------------------------------------------------------------------------- */

/** Entity type → concrete lucide component (README §5 mapping). */
export const ENTITY_ICON_COMPONENT: Record<EntityType, LucideIcon> = {
  person: User,
  company: Building2,
  project: Box,
  quote: ReceiptText,
  order: ClipboardList,
  invoice: Receipt,
  contract: FileSignature,
  document: FileText,
  mailbox: Mail,
  mail_thread: MessagesSquare,
  source_item: File,
  meeting: CalendarClock,
};

/** Evidence kind → concrete lucide component. */
export const EVIDENCE_ICON_COMPONENT: Record<EvidenceKind, LucideIcon> = {
  mail: Mail,
  contract: FileSignature,
  invoice: Receipt,
  quote: ReceiptText,
  order: ClipboardList,
  document: FileText,
  source: File,
};

/** Evidence source channel → concrete lucide component (matches the connector grid). */
export const EVIDENCE_CHANNEL_ICON_COMPONENT: Record<
  EvidenceChannel,
  LucideIcon
> = {
  mail: Mail,
  calendar: Calendar,
  teams: MessagesSquare,
};

/**
 * Relation → the glyph its edge label draws instead of its words.
 *
 * Every label rendered the relation in full, and on a mail-heavy tenant that
 * meant "Emailed" printed a dozen times across one neighbourhood, over the
 * names of the very nodes it was describing. The word is the same word each
 * time; the map only needs to say *which kind* of link this is.
 *
 * Each entry is a distinct **shape**. They all draw in the same accent, so two
 * relations may never be told apart by colour — a reader who cannot separate
 * hues must still separate an envelope from a handshake. A relation outside
 * this map keeps its words rather than borrowing a glyph that would say
 * something else; the words are never dropped, only folded (see EdgeLabels).
 */
export const RELATION_ICON_COMPONENT: Record<string, LucideIcon | undefined> = {
  sent_to: Mail,
  reply_to: Reply,
  authored_by: PenLine,
  attached_to: Paperclip,
  committed_to: Handshake,
  decided: Gavel,
  belongs_to_project: Box,
  quote_for: ReceiptText,
  invoice_for: Receipt,
  payment_due: CreditCard,
  delivery_delayed: Truck,
  complains_about: MessageSquareWarning,
  supersedes: Replace,
  invited_to: CalendarPlus,
  participated_in: Users,
};

/**
 * Resolves a design-system `Tone` to its `--obsina-*` custom properties so a
 * marker/soft/tone value can be read from CSS. This mirrors the design system's
 * own `toneProperties`, kept local because the package re-exports only the
 * `Tone` type (not the helper). Values are token variables only — never raw hex.
 */
type ToneVars = CSSProperties & {
  "--obsina-tone": string;
  "--obsina-tone-soft": string;
  "--obsina-tone-marker": string;
};

const TONE_VARS: Record<Tone, ToneVars> = {
  neutral: {
    "--obsina-tone": "var(--obsina-text-body)",
    "--obsina-tone-soft": "var(--obsina-surface-sunken)",
    "--obsina-tone-marker": "var(--obsina-text-muted)",
  },
  accent: {
    "--obsina-tone": "var(--obsina-accent-text)",
    "--obsina-tone-soft": "var(--obsina-accent-soft)",
    "--obsina-tone-marker": "var(--obsina-accent)",
  },
  success: {
    "--obsina-tone": "var(--obsina-success)",
    "--obsina-tone-soft": "var(--obsina-success-soft)",
    "--obsina-tone-marker": "var(--obsina-success-marker)",
  },
  warning: {
    "--obsina-tone": "var(--obsina-warning)",
    "--obsina-tone-soft": "var(--obsina-warning-soft)",
    "--obsina-tone-marker": "var(--obsina-warning-marker)",
  },
  danger: {
    "--obsina-tone": "var(--obsina-danger)",
    "--obsina-tone-soft": "var(--obsina-danger-soft)",
    "--obsina-tone-marker": "var(--obsina-danger-marker)",
  },
  info: {
    "--obsina-tone": "var(--obsina-info)",
    "--obsina-tone-soft": "var(--obsina-info-soft)",
    "--obsina-tone-marker": "var(--obsina-info-marker)",
  },
};

export function toneVars(tone: Tone): ToneVars {
  return TONE_VARS[tone];
}

/** Formats an instant, or null when it is absent or not a real timestamp. */
export function formatInstant(
  value: string | null,
  locale: string,
): string | null {
  if (!value) return null;
  const date = new Date(value);
  return Number.isNaN(date.getTime())
    ? null
    : new Intl.DateTimeFormat(locale, {
        dateStyle: "medium",
        timeStyle: "short",
      }).format(date);
}

/** Formats a date (no time), or null when absent/invalid. */
export function formatDate(
  value: string | null,
  locale: string,
): string | null {
  if (!value) return null;
  const date = new Date(value);
  return Number.isNaN(date.getTime())
    ? null
    : new Intl.DateTimeFormat(locale, { dateStyle: "medium" }).format(date);
}

/**
 * Localized text for a status badge. Never colour alone — every badge/spill
 * renders this string beside its marker dot. `dueSoon` formats the ISO instant;
 * if it cannot be parsed the raw value is passed through rather than dropped.
 */
export function badgeText(
  badge: NodeStatusBadge,
  messages: MemoryGraphMessages,
  locale: string,
): string {
  if (badge.kind === "overdue") {
    return messages.badge.overdue(badge.overdueDays);
  }
  return messages.badge.dueSoon(formatDate(badge.dueAt, locale) ?? badge.dueAt);
}

/**
 * One sentence stating what a `partial` map does not contain, or null for any
 * other coverage state.
 *
 * A partial answer is drawn in full: every node and edge on it is real,
 * authorized and evidence-backed, and only recall is bounded. So the shortfall
 * is stated *beside* the map rather than replacing it — the opposite of the
 * unknown surface. The lead carries whether waiting can still close the gap, the
 * counts come from the envelope (a layer it stated nothing about contributes no
 * count, never a fabricated zero), and the reasons follow verbatim from the same
 * vocabulary the unknown surface lists. Exported because the narrow layout,
 * which draws no stage at all, has to say exactly the same thing.
 *
 * The required-source tally the commitment surface prints is deliberately left
 * out here. Under `partial` it is always "all of them" — that is what makes the
 * state partial rather than unknown — and every line this sentence gains is a
 * line the map loses, since the strip and the stage share one column.
 */
export interface CoveragePartialNotice {
  /** One clause. What the strip always shows. */
  readonly summary: string;
  /** Everything the summary stands for, in the reader's words. Never empty. */
  readonly details: readonly string[];
}

/**
 * A coverage sentence for one reason token, and never the token.
 *
 * `unknown.reasons` is total over {@link CoverageReason}, so a term this build
 * knows always has a sentence. A term it does not know is one the API added
 * after this build shipped — that produces the unnamed-constraint sentence,
 * because printing `mail_folder_scope_limited` at a reader is printing an
 * identifier at someone who has no way to look it up. It did: measured on the
 * staging map on 2026-08-07, the token stood on the strip between two ordinary
 * sentences.
 */
export function reasonSentence(
  reason: string,
  messages: MemoryGraphMessages,
): string {
  return (
    messages.unknown.reasons[reason as CoverageReason] ??
    messages.partial.unnamedGap
  );
}

/**
 * What the strip says about bounded recall, split into what is always on screen
 * and what one click reveals.
 *
 * It used to be one string: the durability lead, the derived counts and every
 * gap sentence joined end to end. On the live tenant that was 72px of strip —
 * three lines of counts and clauses under a map — and none of it was the one
 * thing a reader wanted at a glance, which is whether what they are looking at
 * is bounded. So the clause that answers that is the summary, and the rest is
 * kept whole behind the disclosure rather than dropped.
 */
export function coveragePartialNotice(
  coverage: KnowledgeCoverage,
  messages: MemoryGraphMessages,
): CoveragePartialNotice | null {
  if (coverage.state !== "partial") return null;
  const durability = coverage.durability ?? "unstated";
  const details: string[] = [messages.partial.lead[durability]];
  if (coverage.media) {
    details.push(
      messages.partial.media(
        coverage.media.settledCount,
        coverage.media.itemCount,
      ),
    );
  }
  if (coverage.knowledge) {
    details.push(
      messages.partial.knowledge(
        coverage.knowledge.extractedCount,
        coverage.knowledge.versionCount,
      ),
    );
  }
  for (const reason of coverage.reasons) {
    details.push(reasonSentence(reason, messages));
  }
  return { summary: messages.partial.summary[durability], details };
}

/* ------------------------------------------------------------------------- */

const REF_W = LAYOUT_REFERENCE.width;
const REF_H = LAYOUT_REFERENCE.height;

/**
 * Diacritic- and case-insensitive fold used to match a typed query against an
 * entity label.
 *
 * The plain `toLowerCase().includes()` this replaces matched only ASCII-spelled
 * names: `"İlker".toLowerCase()` is `i` + U+0307 (a combining dot), so it never
 * contains `"ilker"`, and `"Öztürk"` never contains `"ozturk"`. On a Turkish
 * tenant that is most of the map — typing a name simply did nothing. NFD splits
 * the marks off so stripping them leaves the base letters; dotless `ı` carries
 * no combining mark of its own and is folded explicitly.
 */
export function foldForSearch(value: string): string {
  return value
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .replace(/ı/g, "i");
}

/**
 * Turns a vocabulary code the message map does not carry into something a
 * reader can read: `payment_due` → "Payment due".
 *
 * The previous fallback printed the code itself. Dropping the edge would be a
 * silent omission — the reason that fallback existed — but a database term on
 * screen is not a name, it is the absence of one, and the reader has no way to
 * tell a real relation from a leak of internal vocabulary. Reshaping the code
 * keeps every bit of the information and shows none of the punctuation.
 */
export function humanizeRelationCode(relationType: string): string {
  const words = relationType.replace(/[_-]+/g, " ").trim();
  if (words === "") return relationType;
  return words.charAt(0).toUpperCase() + words.slice(1);
}

/**
 * Human wording for an edge's relation. A code outside the message map is
 * reshaped by {@link humanizeRelationCode} rather than printed raw.
 */
export function relationLabel(
  relationType: string,
  messages: MemoryGraphMessages,
): string {
  return (
    (messages.relationType as Record<string, string | undefined>)[
      relationType
    ] ?? humanizeRelationCode(relationType)
  );
}

/**
 * The relation as a sentence, read in the direction the record states it:
 * "Nordwind GmbH emailed Miray Aksoy".
 *
 * A reader looking at a line between two circles is asking what the line
 * *means*, and neither `sent_to` nor the two-word chip "Emailed" answers that on
 * its own — the chip names the kind of link but leaves the reader to work out
 * which end is which. The verb map carries the direction; an unmapped code falls
 * back to a neutral verb rather than to the code, so a sentence is always
 * readable even for a relation this build has never been taught.
 */
export function relationSentence(
  relationType: string | null,
  sourceLabel: string,
  targetLabel: string,
  messages: MemoryGraphMessages,
): string {
  const verb =
    (relationType === null
      ? null
      : (messages.relationVerb as Record<string, string | undefined>)[
          relationType
        ]) ?? messages.relationVerbUnknown;
  return messages.relationSentence(sourceLabel, verb, targetLabel);
}

/* ------------------------------------------------------------------------- *
 * Pan / fit (P1 fix: zoom existed with no way to reach an off-center node —
 * `.mg-stage` clips with `overflow: hidden` and the scene only ever scaled
 * about its own center, so a node away from center became permanently
 * unreachable once zoomed. `.mg-scene` now also translates; every zoom-
 * changing action below recomputes that translate so the relevant subject
 * (the focused node, or the whole graph for Fit) stays inside the visible
 * stage instead of drifting under the clip.
 * ------------------------------------------------------------------------- */

/**
 * How far the scene may travel, as a percent of the stage, at a given zoom.
 *
 * A flat bound was wrong at both ends of the new, much wider zoom range. Zoomed
 * out, the whole graph is smaller than the stage and a fixed 65% let a drag
 * fling it clean off the screen with nothing to drag back toward — the "it does
 * not behave the way it should" of an empty canvas. Zoomed in, the scene extends
 * `(zoom − 1) / 2` past each edge and a bound that did not grow with it made the
 * far side unreachable. The allowance is that overhang plus a constant, which
 * covers the inspector-avoidance offset and a recenter onto an edge-of-layout
 * node at any scale.
 */
const PAN_SLACK_PERCENT = 55;

function maxPanPercent(zoom: number): number {
  return 50 * Math.max(0, zoom - 1) + PAN_SLACK_PERCENT;
}
/** Pointer movement (percent of stage) before a press is treated as a drag
 * rather than a click, so a plain tap on a node still opens it. */
const DRAG_THRESHOLD_PERCENT = 1.2;
/** Keyboard pan step, percent of stage per arrow-key press. */
const PAN_KEY_STEP_PERCENT = 8;
/** Margin around the graph's real bounding box that "Fit" leaves visible. */
const FIT_PADDING = 1.12;
/** Mirrors `.mg-inspector`'s CSS box model (memory-graph.css) so the pan math
 * can reserve exactly the screen band the non-modal panel will cover. */
const INSPECTOR_MAX_WIDTH_PX = 384;
const INSPECTOR_SIDE_MARGIN_PX = 28; // `inline-size: min(384px, calc(100% - 28px))`
const INSPECTOR_INSET_PX = 14; // `inset-inline-end: 14px`

/* ------------------------------------------------------------------------- *
 * Stage sizing
 *
 * The stage used to be a fixed 1440×840 box: whatever height that aspect
 * produced at the current width was the map, and the page scrolled around it.
 * A stylesheet rule rescued the case above 961 CSS px by making the whole view
 * a viewport-tall grid, which left every window between the narrow fallback and
 * that breakpoint drawing a short band inside a scrolling document — a second,
 * accidental pan surface wrapped around the real one.
 *
 * The stage now states its own height from what the viewport actually leaves
 * below it. That is the same answer the grid rule gives where the grid applies
 * (so the two never fight), and a real answer where it does not.
 * ------------------------------------------------------------------------- */

/** Never shorter than this, however little room is left; below it the narrow
 *  cluster list is the honest surface and the media query hands over. */
const STAGE_MIN_HEIGHT_PX = 320;
/** Air between the view's bottom strip and whatever the page reserves below it. */
const STAGE_BOTTOM_GAP_PX = 4;

/**
 * Space every ancestor reserves under `node` — their bottom padding, border and
 * this element's own bottom margin, all the way to the body.
 *
 * Guessing a constant here is what turns "fills the viewport" into "fills the
 * viewport plus ten pixels of scrollbar": the page's own padding is a real part
 * of the room the stage does not get, and it changes with the viewport (the
 * shell pads by `clamp(1rem, 2vw, 1.5rem)`). None of these depend on the stage's
 * height, so reading them back cannot feed the resize observer that calls this.
 */
function reservedBelow(node: HTMLElement): number {
  let total = 0;
  let current: HTMLElement | null = node;
  while (current && current !== document.body) {
    const parent: HTMLElement | null = current.parentElement;
    if (!parent) break;
    const own = getComputedStyle(current);
    const around = getComputedStyle(parent);
    total +=
      (Number.parseFloat(own.marginBottom) || 0) +
      (Number.parseFloat(around.paddingBottom) || 0) +
      (Number.parseFloat(around.borderBottomWidth) || 0);
    current = parent;
  }
  return total;
}
/** Sub-pixel churn must not re-write the variable and re-trigger the observer. */
const STAGE_HEIGHT_EPSILON_PX = 1;
/** Below this the stage is not drawn at all (see `NARROW_QUERY` in the view). */
const STAGE_MEASURE_MIN_WIDTH_PX = 721;
/** Relation sentences the hover card states before it starts counting. */
const PEEK_MAX_SENTENCES = 2;
/** Idle time after the last wheel event before the scene animates again. */
const WHEEL_SETTLE_MS = 140;
/** Wheel notches are coarse; a full notch should not jump a whole zoom step. */
const WHEEL_ZOOM_SENSITIVITY = 0.0022;

function clampPan(
  pan: { x: number; y: number },
  zoom: number,
): { x: number; y: number } {
  const limit = maxPanPercent(zoom);
  return {
    x: Math.min(limit, Math.max(-limit, pan.x)),
    y: Math.min(limit, Math.max(-limit, pan.y)),
  };
}

function clampZoom(value: number): number {
  return Number(
    Math.min(GRAPH_ZOOM.max, Math.max(GRAPH_ZOOM.min, value)).toFixed(3),
  );
}

/**
 * The `.mg-scene` translate (percent of its own box, matching CSS `%` for
 * `translate`) that lands normalized `point` at normalized `target` under
 * `zoom`. `.mg-scene` is `transform: translate(...) scale(...)`: CSS composes
 * the rightmost function first, so a point is scaled about the (center)
 * transform-origin before the translate is applied in that same, unscaled
 * percent frame — hence the `* zoom` only on the point's offset from center.
 */
function centeringPan(
  point: NormalizedPoint,
  target: NormalizedPoint,
  zoom: number,
): { x: number; y: number } {
  const solve = (pointFrac: number, targetFrac: number): number =>
    targetFrac * 100 - 50 - (pointFrac * 100 - 50) * zoom;
  return { x: solve(point.x, target.x), y: solve(point.y, target.y) };
}

/* ------------------------------------------------------------------------- *
 * Label decluttering
 *
 * Two things were wrong with the names on this map, and the second hid the
 * first. Every node drew its label at every zoom, so a dense neighbourhood
 * printed forty of them into the same band of pixels — the run of interleaved
 * words across the top of the map. And zooming in did not help, because the text
 * was laid out inside the scaled scene: the geometry spread and the type grew
 * with it, so the same names stayed exactly as tangled, only bigger.
 *
 * The type now holds its screen size across the whole zoom range (`--mg-zoom`
 * counter-scales it in CSS), which makes zoom a real decluttering gesture. On
 * top of that, the labels that would *still* collide at the current scale are
 * left undrawn, most-connected first. Nothing is lost: the name is in the node's
 * accessible name at all times, and pointing at or focusing a node brings its
 * label back regardless (see memory-graph.css).
 *
 * The pass compared names only with other names, which is why the map still
 * read as a tangle after it: a name has no quarrel with the name beside it, it
 * has a quarrel with the CIRCLE it is printed across. On a 151-entity graph the
 * 84 surviving names crossed a neighbour's mark 89 times — nearly every drawn
 * name sat on somebody else's node. The circles are now occupied space too.
 * ------------------------------------------------------------------------- */

/** Mirrors `.mg-node__label`: 18ch cap, ~6.3 px/char at 12 px, 8 px of box. */
const LABEL_MAX_CHARS = 18;
const LABEL_CHAR_PX = 6.3;
const LABEL_BIG_CHAR_PX = 6.9;
const LABEL_BOX_PAD_PX = 8;
const LABEL_LINE_PX = 15;
/** `margin-block-start` between a node's circle and its label. */
const LABEL_GAP_PX = 7;
/** Air demanded between two labels before both may be drawn. */
const LABEL_CLEARANCE_PX = 4;

const TIER_PRIORITY: Record<GraphNode["sizeTier"], number> = {
  hub: 0,
  major: 1,
  regular: 2,
  minor: 3,
};

interface Box {
  readonly left: number;
  readonly right: number;
  readonly top: number;
  readonly bottom: number;
}

function overlaps(a: Box, b: Box): boolean {
  return (
    a.left < b.right && b.left < a.right && a.top < b.bottom && b.top < a.bottom
  );
}

/**
 * The node ids whose labels must not be drawn at this zoom and stage size.
 *
 * Pure and deterministic: the same nodes, layout, zoom and stage always produce
 * the same set, so the map does not flicker between renders and a test can pin
 * it. Importance order is the same one the cluster contract uses — the
 * best-connected name survives a crowd — with the id as the final tie-break so
 * nothing depends on array order.
 */
export function hiddenLabelIds(
  nodes: readonly GraphNode[],
  layoutById: ReadonlyMap<string, LayoutResult["nodes"][number]>,
  zoom: number,
  stage: { width: number; height: number },
): ReadonlySet<string> {
  const hidden = new Set<string>();
  if (stage.width <= 0 || stage.height <= 0 || zoom <= 0) return hidden;
  const ranked = [...nodes].sort(
    (left, right) =>
      TIER_PRIORITY[left.sizeTier] - TIER_PRIORITY[right.sizeTier] ||
      right.linkCount - left.linkCount ||
      right.evidenceCount - left.evidenceCount ||
      (left.id < right.id ? -1 : left.id > right.id ? 1 : 0),
  );
  // Every circle on the map, in the same screen frame the names are measured
  // in. These are placed before the first name is, because a mark is not
  // negotiable: it is the thing the name is about.
  const marks = new Map<string, Box>();
  for (const node of nodes) {
    const layoutNode = layoutById.get(node.id);
    if (!layoutNode) continue;
    const centerX = zoom * layoutNode.position.x * stage.width;
    const centerY = zoom * layoutNode.position.y * stage.height;
    // A node's radius is a fraction of the stage WIDTH in both directions —
    // the circle keeps `aspect-ratio: 1` — so the same number bounds it
    // vertically and horizontally.
    const radius = zoom * layoutNode.radius * stage.width;
    marks.set(node.id, {
      left: centerX - radius,
      right: centerX + radius,
      top: centerY - radius,
      bottom: centerY + radius,
    });
  }
  const placed: Box[] = [];
  for (const node of ranked) {
    const layoutNode = layoutById.get(node.id);
    if (!layoutNode) continue;
    const isBig = node.sizeTier === "hub" || node.sizeTier === "major";
    const width =
      Math.min(node.label.length, LABEL_MAX_CHARS) *
        (isBig ? LABEL_BIG_CHAR_PX : LABEL_CHAR_PX) +
      LABEL_BOX_PAD_PX;
    // Screen geometry: scene offsets scale with the zoom, the type does not.
    const centerX = zoom * layoutNode.position.x * stage.width;
    const top =
      zoom *
      (layoutNode.position.y * stage.height +
        layoutNode.radius * stage.width +
        LABEL_GAP_PX);
    const box: Box = {
      left: centerX - width / 2 - LABEL_CLEARANCE_PX,
      right: centerX + width / 2 + LABEL_CLEARANCE_PX,
      top,
      bottom: top + LABEL_LINE_PX + LABEL_CLEARANCE_PX,
    };
    // Its own mark sits directly above the name and never overlaps it, so the
    // node is skipped rather than tested against itself.
    let collides = placed.some((other) => overlaps(box, other));
    if (!collides) {
      for (const [markId, mark] of marks) {
        if (markId === node.id) continue;
        if (overlaps(box, mark)) {
          collides = true;
          break;
        }
      }
    }
    if (collides) hidden.add(node.id);
    else placed.push(box);
  }
  return hidden;
}

/**
 * Horizontal center of the band left uncovered by the floating, non-modal
 * inspector panel (P1 fix: the panel permanently covers the graph's right
 * ~27%, and with no pan a focused node's neighbors on that side were simply
 * unreachable). Centering a focused node here instead of at the true 50%
 * keeps it and its close neighbors clear of the panel. Falls back to the true
 * center when the stage has not been measured yet (e.g. jsdom in tests).
 */
function safeCenterXFraction(stageWidthPx: number): number {
  if (stageWidthPx <= 0) return 0.5;
  const inspectorWidthPx = Math.min(
    INSPECTOR_MAX_WIDTH_PX,
    Math.max(0, stageWidthPx - INSPECTOR_SIDE_MARGIN_PX),
  );
  const coveredPx = inspectorWidthPx + INSPECTOR_INSET_PX;
  const coveredFraction = Math.min(0.85, coveredPx / stageWidthPx);
  return (1 - coveredFraction) / 2;
}

export interface GraphViewProps {
  readonly data: MemoryGraphData;
  readonly layout: LayoutResult;
  readonly interaction: GraphInteractionState;
  readonly messages: MemoryGraphMessages;
  /** Controlled search value (owned by G4). */
  readonly searchValue: string;
  /** True while the first graph fetch is in flight; shows plain loading text. */
  readonly loading?: boolean;
  /**
   * How many drawn entities the current query matches; null while the box is
   * empty. Zero is stated on the search bar rather than left silent.
   */
  readonly searchMatchCount?: number | null;
  /**
   * Rendered as the stage's last child. The inspector goes here so the panel is
   * bounded by the map it floats over — as a sibling of the whole view it was
   * sized against the stage *plus* the bottom strip, overhanging the canvas by
   * exactly the strip's height and covering the strip's right half.
   */
  readonly overlay?: ReactNode;
  readonly onFocusNode: (nodeId: string | null) => void;
  readonly onHoverNode: (nodeId: string | null) => void;
  readonly onZoomChange: (zoom: number) => void;
  readonly onSearchChange: (value: string) => void;
}

/**
 * Hovering used to leave every other edge at full strength, so pointing at a hub
 * highlighted its links inside an unchanged thicket and isolated nothing. A
 * pointed-at neighbourhood now dims the rest exactly as a focused one does —
 * that separation is what makes a line followable at all.
 */
function edgeState(
  edge: LayoutEdge,
  focusedNodeId: string | null,
  hoveredNodeId: string | null,
): "default" | "active" | "hot" | "faded" {
  const touches = (id: string | null): boolean =>
    id !== null && (edge.sourceNodeId === id || edge.targetNodeId === id);
  if (focusedNodeId) return touches(focusedNodeId) ? "active" : "faded";
  if (hoveredNodeId) return touches(hoveredNodeId) ? "hot" : "faded";
  return "default";
}

export function GraphView({
  data,
  layout,
  interaction,
  messages,
  searchValue,
  loading = false,
  searchMatchCount = null,
  overlay,
  onFocusNode,
  onHoverNode,
  onZoomChange,
  onSearchChange,
}: GraphViewProps) {
  const { locale } = useObsinaI18n();
  const { focusedNodeId, hoveredNodeId, zoom } = interaction;
  const searchInputRef = useRef<HTMLInputElement | null>(null);
  const viewRef = useRef<HTMLElement | null>(null);
  const stageRef = useRef<HTMLDivElement | null>(null);
  const sceneRef = useRef<HTMLDivElement | null>(null);
  /** The layer that carries `--mg-zoom`; see `writeLabelScale`. */
  const nodesRef = useRef<HTMLDivElement | null>(null);
  const stageSizeRef = useRef<{ width: number; height: number }>({
    width: 0,
    height: 0,
  });
  /**
   * Pan is deliberately NOT React state.
   *
   * It used to be, and every pointer move of a drag therefore re-rendered the
   * whole scene — 24 node buttons, 44 edge paths and their labels — to change
   * one `transform`. The layout is computed once and a pan is a transform, so
   * the transform is written straight to the node that carries it and React is
   * left out of the gesture entirely. The layout effect below re-states it after
   * every render, so React can never clobber it.
   */
  const panRef = useRef<{ x: number; y: number }>({ x: 0, y: 0 });
  const panningRef = useRef(false);
  const dragRef = useRef<{
    pointerId: number;
    startClientX: number;
    startClientY: number;
    startPan: { x: number; y: number };
    moved: boolean;
  } | null>(null);
  const suppressNextClickRef = useRef(false);
  const prevFocusedNodeIdRef = useRef<string | null>(null);
  /** Cleared once the wheel goes quiet, which re-arms the scene's transition. */
  const wheelSettleRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  /**
   * The measured stage, as state rather than only a ref: the label declutter
   * below is a render-time decision and has to re-run when the box changes.
   * Written only on a real change, so the observer cannot feed itself.
   */
  const [stageBox, setStageBox] = useState<{ width: number; height: number }>({
    width: 0,
    height: 0,
  });
  /** Edge the pointer is resting on, as an index into `layout.edges`. */
  const [hoveredEdgeIndex, setHoveredEdgeIndex] = useState<number | null>(null);
  const [coverageDetailOpen, setCoverageDetailOpen] = useState(false);
  /**
   * The scale currently PAINTED, which during a continuous gesture runs ahead
   * of the `zoom` prop.
   *
   * A trackpad pinch emits an event every few milliseconds, and every one of
   * them used to be published to the parent's state — so a single one-second
   * gesture re-rendered the whole view, all 151 node marks and 141 edge paths,
   * about eighty times, to change one `transform` that this component had
   * already written by hand. Measured: 270 ms of script and 192.9 ms of style
   * recalc in a pinch, with 11 frames of 121 over budget.
   *
   * The gesture now owns the scale and hands it to React once, when the wheel
   * goes quiet ({@link WHEEL_SETTLE_MS}). Discrete actions — the +/− buttons,
   * Fit, a focus recenter — still publish immediately: there is no stream of
   * them to coalesce, and the label declutter should answer a button press at
   * once.
   */
  const zoomRef = useRef(zoom);
  /** The last `zoom` prop this component saw, so a stale prop arriving mid-
   *  gesture cannot pull the painted scale back to where the parent still
   *  thinks it is. Only a genuine change from outside adopts the prop. */
  const propZoomRef = useRef(zoom);
  if (propZoomRef.current !== zoom) {
    propZoomRef.current = zoom;
    zoomRef.current = zoom;
  }

  /**
   * Publishes the scale the node names divide themselves by, on the smallest
   * element that can carry it.
   *
   * `--mg-zoom` is an inherited custom property, so writing it invalidates the
   * computed style of the whole subtree beneath the element it is set on. On
   * `.mg-scene` that subtree is everything the map draws — 1671 elements at a
   * real tenant's size — of which the 151 names are the only readers. Moving
   * it down to their own layer took a trackpad pinch from 196.6 ms of style
   * recalculation to 157.3 ms with nothing on screen changed.
   *
   * A skip-if-close guard was measured here too and removed: at any threshold
   * small enough to be invisible (2%, 5%) it saved nothing, because a single
   * pinch event already moves the scale about 9%, and the one threshold that
   * did pay (10%, −70 ms) let the type visibly step. An optimization that only
   * works where it can be seen is not one.
   */
  const writeLabelScale = useCallback((scale: number): void => {
    nodesRef.current?.style.setProperty("--mg-zoom", String(scale));
  }, []);

  /**
   * Writes the scene's transform (and the drag class) from the refs above.
   * `zoomOverride` lets an action that is *about* to change the zoom paint the
   * final position in one step instead of moving to it under the old scale and
   * being corrected on the next render.
   */
  const applyScene = useCallback(
    (zoomOverride?: number): void => {
      const scene = sceneRef.current;
      if (!scene) return;
      const { x, y } = panRef.current;
      const scale = zoomOverride ?? zoomRef.current;
      // The transform alone is a compositor property: writing it costs no style
      // recalc on any element, which is why a drag is nearly free.
      scene.style.transform = `translate(${x}%, ${y}%) scale(${scale})`;
      scene.classList.toggle("mg-scene--panning", panningRef.current);
      writeLabelScale(scale);
    },
    [writeLabelScale],
  );

  const setPan = useCallback(
    (next: { x: number; y: number }, zoomOverride?: number): void => {
      panRef.current = clampPan(next, zoomOverride ?? zoomRef.current);
      applyScene(zoomOverride);
    },
    [applyScene],
  );

  // Re-states the transform after every render. The scene's `style` is not a
  // React-managed prop, so this is the only writer — mount, a zoom change from
  // the parent, and a scene that remounts when the data flips all land here.
  useLayoutEffect(() => {
    applyScene();
  });

  // The advertised ⌘K/Ctrl+K shortcut really works: it moves focus to the
  // graph search input (a displayed shortcut with no handler would be a dead
  // affordance, which the design contract forbids).
  useEffect(() => {
    const onShortcut = (event: globalThis.KeyboardEvent) => {
      if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === "k") {
        event.preventDefault();
        searchInputRef.current?.focus();
      }
    };
    window.addEventListener("keydown", onShortcut);
    return () => window.removeEventListener("keydown", onShortcut);
  }, []);

  // Tracks the rendered (untransformed) stage box so pointer-drag deltas and
  // the inspector-avoidance offset can be expressed in the same percent units
  // CSS `translate: %` uses. Read from `.mg-stage`, never `.mg-scene`, because
  // the scene's own box is what the transform moves/scales — measuring it
  // would return a post-transform size and corrupt the math below.
  //
  // The same pass states the stage's height. `--mg-stage-h` is what the
  // viewport actually leaves under the view's own top edge, minus the bottom
  // strip and a little air; the stylesheet falls back to the reference aspect
  // while the variable is unset (a var() that resolves to nothing computes to
  // `auto`), so a first paint before measurement is the old behaviour rather
  // than a collapsed box. Writing it only on a real change keeps the observer
  // from feeding itself.
  useEffect(() => {
    const node = stageRef.current;
    if (!node) return;
    const measure = (): void => {
      const rect = node.getBoundingClientRect();
      stageSizeRef.current = { width: rect.width, height: rect.height };
      setStageBox((current) =>
        Math.abs(current.width - rect.width) <= STAGE_HEIGHT_EPSILON_PX &&
        Math.abs(current.height - rect.height) <= STAGE_HEIGHT_EPSILON_PX
          ? current
          : { width: rect.width, height: rect.height },
      );
      const view = viewRef.current;
      if (!view) return;
      const viewTop = view.getBoundingClientRect().top;
      const stripHeight =
        view.querySelector(".mg-botstrip")?.getBoundingClientRect().height ?? 0;
      if (window.innerWidth < STAGE_MEASURE_MIN_WIDTH_PX) {
        view.style.removeProperty("--mg-stage-h");
        return;
      }
      const available = Math.round(
        window.innerHeight -
          viewTop -
          stripHeight -
          reservedBelow(view) -
          STAGE_BOTTOM_GAP_PX,
      );
      const next = Math.max(STAGE_MIN_HEIGHT_PX, available);
      const current = Number.parseFloat(
        view.style.getPropertyValue("--mg-stage-h"),
      );
      if (
        Number.isFinite(current) &&
        Math.abs(current - next) <= STAGE_HEIGHT_EPSILON_PX
      ) {
        return;
      }
      view.style.setProperty("--mg-stage-h", `${next}px`);
    };
    measure();
    if (typeof ResizeObserver === "undefined") {
      window.addEventListener("resize", measure);
      return () => window.removeEventListener("resize", measure);
    }
    const observer = new ResizeObserver(measure);
    observer.observe(node);
    if (viewRef.current) observer.observe(viewRef.current);
    window.addEventListener("resize", measure);
    return () => {
      observer.disconnect();
      window.removeEventListener("resize", measure);
    };
  }, []);

  // A wheel gesture in flight when the view goes away must not fire into a
  // scene that no longer exists.
  useEffect(
    () => () => {
      if (wheelSettleRef.current) clearTimeout(wheelSettleRef.current);
    },
    [],
  );

  // Adjacency drives the focus/hover neighbourhoods; both directions counted.
  const adjacency = useMemo(() => {
    const map = new Map<string, Set<string>>();
    const link = (a: string, b: string): void => {
      (map.get(a) ?? map.set(a, new Set()).get(a)!).add(b);
    };
    for (const edge of layout.edges) {
      link(edge.sourceNodeId, edge.targetNodeId);
      link(edge.targetNodeId, edge.sourceNodeId);
    }
    return map;
  }, [layout.edges]);

  const layoutById = useMemo(() => {
    const map = new Map<string, LayoutResult["nodes"][number]>();
    for (const node of layout.nodes) map.set(node.nodeId, node);
    return map;
  }, [layout.nodes]);

  // Names, by id — what turns an edge from a line between two circles into a
  // sentence. Read off the drawn graph only, so nothing here can name an entity
  // the map does not hold.
  const nodeById = useMemo(() => {
    const map = new Map<string, GraphNode>();
    if (data.status === "ok")
      for (const node of data.nodes) map.set(node.id, node);
    return map;
  }, [data]);

  // A drag must not also report hover: the pointer sweeps every node it passes,
  // and each report would re-render the neighbourhood mid-gesture for a
  // highlight nobody asked for.
  const reportHover = useCallback(
    (nodeId: string | null): void => {
      if (dragRef.current?.moved) return;
      onHoverNode(nodeId);
    },
    [onHoverNode],
  );

  const onEdgeHover = useCallback((index: number | null): void => {
    if (dragRef.current?.moved) return;
    setHoveredEdgeIndex(index);
  }, []);

  // Recenters on a newly focused entity (click, search match, keyboard
  // activation all funnel through the same `onFocusNode`) so it — and, via
  // `safeCenterXFraction`, the neighbours on its right that the inspector
  // would otherwise sit on top of — land inside the visible stage. Reads
  // `zoomRef` rather than depending on `zoom` so a *later*, unrelated zoom
  // change (the +/- buttons handle their own recenter; Fit computes its own
  // whole-graph pan) never re-fires this and clobbers that action's result.
  // Deliberately resets pan only on a focused → unfocused transition, not on
  // every unrelated re-render while already unfocused, so a manual drag in
  // the overview survives an unrelated data refresh.
  useEffect(() => {
    const placed = focusedNodeId ? layoutById.get(focusedNodeId) : undefined;
    if (focusedNodeId && placed) {
      const targetX = safeCenterXFraction(stageSizeRef.current.width);
      setPan(
        centeringPan(placed.position, { x: targetX, y: 0.5 }, zoomRef.current),
      );
    } else if (prevFocusedNodeIdRef.current !== null && !focusedNodeId) {
      setPan({ x: 0, y: 0 });
    }
    prevFocusedNodeIdRef.current = focusedNodeId;
  }, [focusedNodeId, layoutById, setPan]);

  // Which names the map has room for at this scale. Recomputed only when the
  // scale or the stage changes — not on hover, not on a pan.
  const hiddenLabels = useMemo(
    () =>
      data.status === "ok"
        ? hiddenLabelIds(data.nodes, layoutById, zoom, stageBox)
        : new Set<string>(),
    [data, layoutById, zoom, stageBox],
  );

  const focusNeighbors = focusedNodeId ? adjacency.get(focusedNodeId) : null;
  const hoverNeighbors =
    !focusedNodeId && hoveredNodeId ? adjacency.get(hoveredNodeId) : null;

  const nodeState = (nodeId: string): NodeRenderState => {
    if (!focusedNodeId) return "default";
    if (nodeId === focusedNodeId) return "focused";
    return focusNeighbors?.has(nodeId) ? "neighbor" : "faded";
  };

  // Zoom in/out keep the current subject in view: when a node is focused, the
  // translate is recomputed for the *new* zoom so that node (clear of the
  // inspector) stays centered instead of drifting toward the clipped edge —
  // the literal P1 bug (zoom with no compensating pan).
  const zoomTo = (next: number): void => {
    const clamped = clampZoom(next);
    zoomRef.current = clamped;
    if (focusedNodeId) {
      const placed = layoutById.get(focusedNodeId);
      if (placed) {
        const targetX = safeCenterXFraction(stageSizeRef.current.width);
        setPan(
          centeringPan(placed.position, { x: targetX, y: 0.5 }, clamped),
          clamped,
        );
      }
    } else {
      // Nothing focused: the pan is unchanged, but the scale is not — repaint
      // it now rather than leaving the old scale on screen until the parent's
      // state change comes back round.
      applyScene(clamped);
    }
    onZoomChange(clamped);
  };

  // Fits the *real* graph — its measured bounding box, not a fixed preset —
  // into the stage, leaving a small margin. Deliberately independent of the
  // recenter effect above (it sets zoom/pan directly) so it always shows the
  // whole graph, even with a node still focused/inspector open, rather than
  // being overridden back to a single-node recenter.
  const fitToGraph = (): void => {
    if (layout.nodes.length === 0) {
      zoomRef.current = GRAPH_ZOOM.overviewDefault;
      setPan({ x: 0, y: 0 }, GRAPH_ZOOM.overviewDefault);
      onZoomChange(GRAPH_ZOOM.overviewDefault);
      return;
    }
    let minX = Infinity;
    let maxX = -Infinity;
    let minY = Infinity;
    let maxY = -Infinity;
    for (const node of layout.nodes) {
      minX = Math.min(minX, node.position.x - node.radius);
      maxX = Math.max(maxX, node.position.x + node.radius);
      minY = Math.min(minY, node.position.y - node.radius);
      maxY = Math.max(maxY, node.position.y + node.radius);
    }
    const spanX = Math.max(maxX - minX, 0.02);
    const spanY = Math.max(maxY - minY, 0.02);
    const nextZoom = clampZoom(
      Math.min(1 / (spanX * FIT_PADDING), 1 / (spanY * FIT_PADDING)),
    );
    const targetX = focusedNodeId
      ? safeCenterXFraction(stageSizeRef.current.width)
      : 0.5;
    const center: NormalizedPoint = {
      x: (minX + maxX) / 2,
      y: (minY + maxY) / 2,
    };
    zoomRef.current = nextZoom;
    setPan(centeringPan(center, { x: targetX, y: 0.5 }, nextZoom), nextZoom);
    onZoomChange(nextZoom);
  };

  const onScenePointerDown = (event: PointerEvent<HTMLDivElement>): void => {
    if (event.pointerType === "mouse" && event.button !== 0) return;
    dragRef.current = {
      pointerId: event.pointerId,
      startClientX: event.clientX,
      startClientY: event.clientY,
      startPan: panRef.current,
      moved: false,
    };
    // Deliberately no `setPointerCapture` here: in a real browser (unlike
    // jsdom, where it is a no-op and this bug does not reproduce), capturing
    // a pointer that never turns into a drag retargets the browser's own
    // synthesized `click` to the *capturing* element — `.mg-scene` — instead
    // of the node button under it, so a plain tap would never open a node.
    // Capture is requested lazily, only once a real drag is confirmed below.
  };

  const onScenePointerMove = (event: PointerEvent<HTMLDivElement>): void => {
    const drag = dragRef.current;
    if (drag?.pointerId !== event.pointerId) return;
    const { width, height } = stageSizeRef.current;
    if (width <= 0 || height <= 0) return;
    const dxPercent = ((event.clientX - drag.startClientX) / width) * 100;
    const dyPercent = ((event.clientY - drag.startClientY) / height) * 100;
    if (
      !drag.moved &&
      Math.hypot(dxPercent, dyPercent) < DRAG_THRESHOLD_PERCENT
    ) {
      return;
    }
    if (!drag.moved) {
      // Now that this is confirmed to be a drag (not a click), keep
      // pointermove/up routed here even if the pointer strays over a sibling
      // overlay (search bar, zoom control, minimap) mid-drag. Guarded: not
      // implemented in jsdom (unit tests).
      if (typeof event.currentTarget.setPointerCapture === "function") {
        try {
          event.currentTarget.setPointerCapture(event.pointerId);
        } catch {
          // Not supported in this environment; dragging still works via
          // bubbled pointer events as long as the pointer stays over the
          // scene.
        }
      }
    }
    drag.moved = true;
    panningRef.current = true;
    // No React state on this path: the whole gesture is one style write per
    // pointer move, so the scene follows the pointer instead of waiting for a
    // render of every node and edge on the map.
    setPan({
      x: drag.startPan.x + dxPercent,
      y: drag.startPan.y + dyPercent,
    });
  };

  const endScenePointer = (event: PointerEvent<HTMLDivElement>): void => {
    const drag = dragRef.current;
    if (drag?.pointerId !== event.pointerId) return;
    dragRef.current = null;
    panningRef.current = false;
    applyScene();
    // A real drag must not also fire the dragged-over node's click — swallow
    // exactly the next click via the capture-phase handler below.
    if (drag.moved) suppressNextClickRef.current = true;
  };

  const onSceneClickCapture = (event: MouseEvent<HTMLDivElement>): void => {
    if (!suppressNextClickRef.current) return;
    suppressNextClickRef.current = false;
    event.preventDefault();
    event.stopPropagation();
  };

  /**
   * Keeps the scene following the wheel instead of easing toward it.
   *
   * `.mg-scene` carries a 280 ms transition for the discrete moves — the zoom
   * buttons, Fit, a keyboard pan, a focus recenter — and a pointer drag already
   * turned it off for the duration of the gesture. A wheel gesture never did, so
   * every one of the dozens of events a single two-finger swipe emits started
   * its own 280 ms animation toward a target the next event had already moved.
   * That is the mush: the map lagging behind the fingers and coasting past them.
   * The class goes on for the gesture and comes off once the wheel goes quiet.
   */
  const holdWheelGesture = (): void => {
    panningRef.current = true;
    if (wheelSettleRef.current) clearTimeout(wheelSettleRef.current);
    wheelSettleRef.current = setTimeout(() => {
      wheelSettleRef.current = null;
      // The scale the gesture arrived at, handed to React exactly once. Until
      // this line the parent still holds the scale the pinch started from, and
      // the label declutter is answering that older number — which is why it
      // is recomputed here rather than eighty times on the way. Published
      // before the drag guard below: a pinch followed straight by a drag must
      // not leave the parent holding a scale that is no longer on screen.
      if (zoomRef.current !== propZoomRef.current)
        onZoomChange(zoomRef.current);
      // A pointer drag that began mid-settle owns the flag; leave it alone.
      if (dragRef.current) return;
      panningRef.current = false;
      applyScene();
    }, WHEEL_SETTLE_MS);
  };

  /**
   * Scales about a point on the stage, so whatever is under the pointer stays
   * under it. Zooming about the stage centre — the only thing the buttons could
   * do — means aiming at a neighbourhood, zooming, and finding it has slid off
   * the edge; anchoring is what makes the wheel feel like a magnifier.
   *
   * `publish: false` paints the new scale without telling React about it; the
   * caller is then responsible for publishing once its gesture ends.
   */
  const zoomAt = (
    next: number,
    clientX: number,
    clientY: number,
    publish = true,
  ): void => {
    const stage = stageRef.current;
    const { width, height } = stageSizeRef.current;
    const clamped = clampZoom(next);
    const current = zoomRef.current;
    if (!stage || width <= 0 || height <= 0 || clamped === current) {
      if (clamped !== current && publish) onZoomChange(clamped);
      return;
    }
    const rect = stage.getBoundingClientRect();
    // The anchor's offset from the stage centre, as a percent of the stage.
    const anchorX = ((clientX - rect.left) / width) * 100 - 50;
    const anchorY = ((clientY - rect.top) / height) * 100 - 50;
    const ratio = clamped / current;
    const pan = panRef.current;
    zoomRef.current = clamped;
    // Solve for the translate that leaves the anchor's scene point fixed:
    // anchor = pan + ratio · (anchor − pan) ⇒ pan' = anchor − ratio·(anchor − pan).
    setPan(
      {
        x: anchorX - ratio * (anchorX - pan.x),
        y: anchorY - ratio * (anchorY - pan.y),
      },
      clamped,
    );
    if (publish) onZoomChange(clamped);
  };

  /**
   * The wheel does both jobs a map's wheel is expected to do: a pinch (which
   * every browser reports as ctrl+wheel) or Ctrl/⌘+wheel zooms about the
   * pointer, and a plain two-finger swipe pans. Panning still yields the event
   * to the page once the scene has hit its bound, so a map at rest inside a
   * scrolling document never traps the reader.
   */
  const onSceneWheel = (event: WheelEvent<HTMLDivElement>): void => {
    const { width, height } = stageSizeRef.current;
    if (width <= 0 || height <= 0) return;
    if (event.ctrlKey || event.metaKey) {
      if (event.deltaY === 0) return;
      event.preventDefault();
      holdWheelGesture();
      zoomAt(
        zoomRef.current * Math.exp(-event.deltaY * WHEEL_ZOOM_SENSITIVITY),
        event.clientX,
        event.clientY,
        false,
      );
      return;
    }
    const dxPercent = (event.deltaX / width) * 100;
    const dyPercent = (event.deltaY / height) * 100;
    if (dxPercent === 0 && dyPercent === 0) return;
    const pan = panRef.current;
    const next = clampPan(
      { x: pan.x - dxPercent, y: pan.y - dyPercent },
      zoomRef.current,
    );
    if (next.x === pan.x && next.y === pan.y) return;
    event.preventDefault();
    holdWheelGesture();
    setPan(next);
  };

  const onSceneKeyDown = (event: KeyboardEvent<HTMLElement>): void => {
    if (event.key === "Escape" && focusedNodeId) {
      event.stopPropagation();
      onFocusNode(null);
      return;
    }
    // Keyboard alternative for panning (no drag/pointer required). Skipped
    // inside the search field, where arrow keys must move the text cursor.
    const target = event.target;
    const isTextField =
      target instanceof HTMLInputElement ||
      target instanceof HTMLTextAreaElement;
    if (isTextField || event.metaKey || event.ctrlKey || event.altKey) return;
    const step = PAN_KEY_STEP_PERCENT;
    const current = panRef.current;
    if (event.key === "ArrowLeft") {
      event.preventDefault();
      setPan({ x: current.x + step, y: current.y });
    } else if (event.key === "ArrowRight") {
      event.preventDefault();
      setPan({ x: current.x - step, y: current.y });
    } else if (event.key === "ArrowUp") {
      event.preventDefault();
      setPan({ x: current.x, y: current.y + step });
    } else if (event.key === "ArrowDown") {
      event.preventDefault();
      setPan({ x: current.x, y: current.y - step });
    }
  };

  const asOfText = data.asOf
    ? messages.stage.asOf(formatInstant(data.asOf, locale) ?? data.asOf)
    : messages.stage.asOfUnknown;

  const showScene = !loading && data.status === "ok" && data.nodes.length > 0;
  // Bounded-fetch notice: only a drawn scene has a shown-count to bound.
  const truncatedNotice =
    showScene && data.status === "ok" && data.truncated
      ? messages.stage.truncated(data.nodes.length)
      : null;
  // A drawn map whose recall is bounded says so on the same strip that already
  // carries what this view does not contain — including when it drew nothing,
  // where "no entities" would otherwise read as a complete answer of zero.
  const partialNotice =
    data.status === "ok"
      ? coveragePartialNotice(data.coverage, messages)
      : null;
  // A disagreement nobody clicks on would never announce itself: edge labels
  // only appear for the neighbourhood being read. The strip is the one surface
  // that is always on screen, so the count of conflicted relations is stated
  // there, with no node focused and no pointer anywhere near the map.
  const conflictedCount =
    showScene && data.status === "ok"
      ? data.edges.filter((edge) => edge.states?.includes("conflicted")).length
      : 0;
  const showBotstrip =
    showScene || data.status === "unknown" || data.nodes.length === 0;

  // What the pointer is resting on, said in words under the map. An edge under
  // the pointer wins over a node, because pointing at a line is the more
  // specific question of the two.
  const peek = describePeek({
    asOf: data.asOf,
    edge:
      hoveredEdgeIndex === null
        ? null
        : (layout.edges[hoveredEdgeIndex] ?? null),
    layout,
    messages,
    locale,
    node: hoveredNodeId ? (nodeById.get(hoveredNodeId) ?? null) : null,
    nodeById,
  });

  return (
    <section
      aria-label={messages.title}
      className="mg-view"
      onKeyDown={onSceneKeyDown}
      ref={viewRef}
    >
      <div className="mg-stage" ref={stageRef}>
        {loading ? (
          <StatusSurface centered>
            <p aria-busy="true" className="mg-status__body" role="status">
              {messages.loading}
            </p>
          </StatusSurface>
        ) : data.status === "unknown" ? (
          <UnknownSurface data={data} messages={messages} />
        ) : data.nodes.length === 0 ? (
          <EmptySurface
            asOfText={asOfText}
            coverage={data.coverage}
            messages={messages}
          />
        ) : (
          <>
            <div
              className="mg-scene"
              onClickCapture={onSceneClickCapture}
              onPointerCancel={endScenePointer}
              onPointerDown={onScenePointerDown}
              onPointerMove={onScenePointerMove}
              onPointerUp={endScenePointer}
              onWheel={onSceneWheel}
              ref={sceneRef}
            >
              <Edges
                focusedNodeId={focusedNodeId}
                hoveredNodeId={hoveredNodeId}
                layout={layout}
              />
              <EdgeLabels
                focusedNodeId={focusedNodeId}
                hoveredNodeId={hoveredNodeId}
                layout={layout}
                messages={messages}
                onEdgeHover={onEdgeHover}
              />
              <div className="mg-nodes" ref={nodesRef}>
                {data.nodes.map((node) => {
                  const placed = layoutById.get(node.id);
                  if (!placed) return null;
                  return (
                    <NodeMark
                      key={node.id}
                      hovered={hoverNeighbors?.has(node.id) ?? false}
                      labelCrowded={hiddenLabels.has(node.id)}
                      locale={locale}
                      messages={messages}
                      node={node}
                      onFocusNode={onFocusNode}
                      onHoverNode={reportHover}
                      placed={placed}
                      state={nodeState(node.id)}
                    />
                  );
                })}
              </div>
            </div>

            {peek && <PeekCard peek={peek} />}

            {/* The hint and the clear control share one slot: a ⌘K badge beside
                a typed query is a shortcut for something already done, and the
                clear affordance only exists once there is text to clear. The
                browser's own `type="search"` cancel button is suppressed in CSS
                so the visible ✕ is this one — reachable by keyboard, labelled,
                and the only one on screen. */}
            <div className="mg-panel mg-searchbar">
              <Icon icon={Search} size={16} />
              <input
                aria-label={messages.search.label}
                className="mg-search-input"
                onChange={(event) => onSearchChange(event.target.value)}
                placeholder={messages.search.placeholder}
                ref={searchInputRef}
                type="search"
                value={searchValue}
              />
              {searchMatchCount === 0 && (
                <span className="mg-searchbar__miss" role="status">
                  {messages.search.noMatch}
                </span>
              )}
              {searchValue === "" ? (
                <kbd aria-hidden="true" className="mg-kbd">
                  {messages.search.shortcut}
                </kbd>
              ) : (
                <button
                  aria-label={messages.search.clear}
                  className="mg-searchbar__clear"
                  onClick={() => {
                    onSearchChange("");
                    searchInputRef.current?.focus();
                  }}
                  type="button"
                >
                  <Icon icon={X} size={15} />
                </button>
              )}
            </div>

            <div className="mg-zoom">
              <button
                aria-label={messages.zoom.zoomIn}
                className="mg-zoom__button"
                onClick={() => zoomTo(zoomRef.current * GRAPH_ZOOM.factor)}
                type="button"
              >
                <Icon icon={Plus} size={18} />
              </button>
              <button
                aria-label={messages.zoom.zoomOut}
                className="mg-zoom__button"
                onClick={() => zoomTo(zoomRef.current / GRAPH_ZOOM.factor)}
                type="button"
              >
                <Icon icon={Minus} size={18} />
              </button>
              <button
                aria-label={messages.zoom.fit}
                className="mg-zoom__button"
                onClick={fitToGraph}
                type="button"
              >
                <Icon icon={Maximize} size={18} />
              </button>
            </div>
          </>
        )}
        {overlay}
      </div>

      {showBotstrip && (
        <div className="mg-botstrip">
          <span className="mg-botstrip__cell">
            <Icon icon={Clock} size={14} />
            {asOfText}
          </span>
          {truncatedNotice && (
            <span className="mg-botstrip__cell">{truncatedNotice}</span>
          )}
          {partialNotice && (
            <span className="mg-botstrip__cell mg-botstrip__cell--partial">
              <Icon icon={FileSearch} size={14} />
              {partialNotice.summary}
              {/*
                The detail opens over the map rather than inside the strip: the
                stage is sized against the strip's measured height, so a cell
                that grew on click would push the canvas out from under itself.
              */}
              <button
                aria-expanded={coverageDetailOpen}
                className="mg-botstrip__toggle"
                onClick={() => setCoverageDetailOpen((open) => !open)}
                type="button"
              >
                {messages.partial.detailToggle}
              </button>
              {coverageDetailOpen && (
                <ul
                  aria-label={messages.partial.detailLabel}
                  className="mg-botstrip__detail"
                >
                  {partialNotice.details.map((line) => (
                    <li key={line}>{line}</li>
                  ))}
                </ul>
              )}
            </span>
          )}
          {conflictedCount > 0 && (
            <span className="mg-botstrip__cell mg-botstrip__cell--conflict">
              <Icon icon={MessageSquareWarning} size={14} />
              {messages.conflictNotice(conflictedCount)}
            </span>
          )}
          <span className="mg-botstrip__cell">
            <Icon icon={Lock} size={14} />
            {messages.stage.authorizationNote}
          </span>
        </div>
      )}
    </section>
  );
}

/* ------------------------------------------------------------------------- *
 * Everything below is memoized on its own props.
 *
 * The scene used to re-render whole on any change anywhere in the view: a pan,
 * a zoom, a pointer entering a node. Nothing about a node's mark changes when
 * the scene is panned — so each of these now re-renders only when what it draws actually
 * differs, and the map keeps up with the hand moving it.
 * ------------------------------------------------------------------------- */

const Edges = memo(function Edges({
  layout,
  focusedNodeId,
  hoveredNodeId,
}: {
  layout: LayoutResult;
  focusedNodeId: string | null;
  hoveredNodeId: string | null;
}) {
  // The path data depends only on the layout, so it is built once per layout
  // rather than re-concatenated for every edge on every highlight change.
  const paths = useMemo(
    () =>
      layout.edges.map(
        (edge) =>
          `M${edge.start.x * REF_W} ${edge.start.y * REF_H}` +
          ` Q${edge.control.x * REF_W} ${edge.control.y * REF_H}` +
          ` ${edge.end.x * REF_W} ${edge.end.y * REF_H}`,
      ),
    [layout.edges],
  );
  return (
    <svg
      aria-hidden="true"
      className="mg-edges"
      preserveAspectRatio="none"
      viewBox={`0 0 ${REF_W} ${REF_H}`}
    >
      {/* Only the highlighted neighbourhood is given a head. A map where every
          line carries one is a map of arrows; a map where the two or three you
          are pointing at do is a map you can follow. */}
      <defs>
        <marker
          className="mg-edge-arrowhead"
          id="mg-edge-arrow"
          markerHeight="6"
          markerUnits="userSpaceOnUse"
          markerWidth="6"
          orient="auto-start-reverse"
          refX="5.4"
          refY="3"
        >
          <path d="M0 0.6 L6 3 L0 5.4 Z" />
        </marker>
      </defs>
      {layout.edges.map((edge, index) => {
        const state = edgeState(edge, focusedNodeId, hoveredNodeId);
        const highlighted = state === "active" || state === "hot";
        return (
          <path
            className={`mg-edge${state === "default" ? "" : ` mg-edge--${state}`}`}
            d={paths[index]}
            // The stroke pattern is the one signal that is always on screen:
            // labels only appear for the neighbourhood being read, so a
            // relation nobody focuses still has to show what it rests on.
            data-claim-state={severestClaimState(edge.states) ?? undefined}
            data-edge-state={state}
            key={`${edge.sourceNodeId}-${edge.targetNodeId}-${index}`}
            markerEnd={highlighted ? "url(#mg-edge-arrow)" : undefined}
            strokeOpacity={0.22 + edge.weight * 0.06}
            strokeWidth={1 + edge.weight * 0.5}
          />
        );
      })}
    </svg>
  );
});

/**
 * Assertion states, ordered by how loudly the map must say them. `conflicted`
 * leads because a relation its own sources disagree about is the one a reader
 * must not take at face value; `superseded` trails because it is a true record
 * of a past state rather than a doubtful one.
 */
const CLAIM_STATE_RANK: readonly ClaimState[] = [
  "conflicted",
  "uncertain",
  "superseded",
  "supported",
];

/**
 * The state an edge is drawn as: the severest one merged into it. An edge that
 * merges several relations can hold more than one state, and the map has one
 * stroke to spend — so it spends it on the state that most changes what the
 * reader should believe, and {@link claimStateWords} still names every state
 * present in the words.
 */
export function severestClaimState(
  states: readonly ClaimState[] | undefined,
): ClaimState | null {
  if (!states || states.length === 0) return null;
  return CLAIM_STATE_RANK.find((rank) => states.includes(rank)) ?? null;
}

/**
 * Plain wording for every non-ordinary state on an edge, in severity order.
 * `supported` contributes nothing: labelling the ordinary case would make the
 * exceptions quieter, which is the opposite of the point.
 */
export function claimStateWords(
  states: readonly ClaimState[] | undefined,
  messages: MemoryGraphMessages,
): string[] {
  if (!states) return [];
  return CLAIM_STATE_RANK.filter((rank) => states.includes(rank))
    .map((rank) => messages.claimState[rank])
    .filter((words): words is string => words !== null);
}

/** B(0.5) of the edge's own quadratic — the same point the layout anchors a
 * strong edge's label at, for the edges it leaves unanchored. */
function quadraticMidpoint(edge: LayoutEdge): NormalizedPoint {
  const at = (start: number, control: number, end: number): number =>
    0.25 * start + 0.5 * control + 0.25 * end;
  return {
    x: at(edge.start.x, edge.control.x, edge.end.x),
    y: at(edge.start.y, edge.control.y, edge.end.y),
  };
}

/**
 * Relation labels for the neighbourhood the reader is actually pointing at —
 * nothing at rest.
 *
 * Every strong edge used to carry one, so a 62-edge network drew 62 chips
 * reading `sent_to`: the same word repeated over the lines it was meant to
 * distinguish, on top of the node names it buried. A label is only worth its
 * ink when it separates one edge from another, which is exactly the moment a
 * node is focused or hovered. Structure is still visible at rest — the lines and
 * their weights are drawn either way — so nothing is hidden, only deferred to
 * the moment it means something.
 */
const EdgeLabels = memo(function EdgeLabels({
  layout,
  focusedNodeId,
  hoveredNodeId,
  messages,
  onEdgeHover,
}: {
  layout: LayoutResult;
  focusedNodeId: string | null;
  hoveredNodeId: string | null;
  messages: MemoryGraphMessages;
  /** Reports the edge under the pointer so the card below can say what it is. */
  onEdgeHover: (index: number | null) => void;
}) {
  if (!focusedNodeId && !hoveredNodeId) return null;
  return (
    <div className="mg-edge-labels">
      {layout.edges.map((edge, index) => {
        const state = edgeState(edge, focusedNodeId, hoveredNodeId);
        if (state !== "active" && state !== "hot") return null;
        // No glyph for a relation this build cannot name: a shape is a claim
        // about which kind of link this is, and the whole point of the neutral
        // wording below is that this build does not know.
        const glyph = edge.relationType
          ? RELATION_ICON_COMPONENT[edge.relationType]
          : undefined;
        // "Emailed · sources disagree": the relation, then what it rests on.
        // The marker's shape says which relation; only words can say how far to
        // trust it, so the words carry the state and the shape never has to.
        const claim = severestClaimState(edge.states);
        // An edge whose kind is outside this build's vocabulary is drawn and
        // named "Linked" — never skipped. Skipping it left the one line on the
        // map with no name at all beside named ones, which reads as a relation
        // of some other, unstated kind rather than as one nobody can name.
        const words = [
          edge.relationType
            ? relationLabel(edge.relationType, messages)
            : messages.inspector.connectionUnlabeled,
          ...claimStateWords(edge.states, messages),
        ].join(" · ");
        // The layout anchors only the edges it considers strong. Inside the one
        // neighbourhood being read, a thin link is as much a part of the answer
        // as a thick one — an unlabeled edge among labeled ones reads as a
        // relation of some other, unstated kind — so the rest are anchored at
        // the same place: the midpoint of their own quadratic curve.
        const anchor = edge.labelAnchor ?? quadraticMidpoint(edge);
        return (
          // Glyph on the map, words in the accessibility tree and back on
          // screen the moment a pointer rests on it — the reader never has to
          // learn a shape, and never has to read the same word twelve times to
          // find the name underneath it. `title` covers the pointer; the span
          // covers assistive tech, which reads a graph in browse order.
          <span
            className={`mg-edge-label mg-edge-label--${state}${
              glyph ? " mg-edge-label--glyph" : ""
            }`}
            data-claim-state={claim ?? undefined}
            data-edge-label-state={state}
            key={`${edge.sourceNodeId}-${edge.targetNodeId}-${index}`}
            onMouseEnter={() => onEdgeHover(index)}
            onMouseLeave={() => onEdgeHover(null)}
            style={{
              insetInlineStart: `${anchor.x * 100}%`,
              insetBlockStart: `${anchor.y * 100}%`,
            }}
            title={glyph ? words : undefined}
          >
            {glyph && <Icon icon={glyph} size={13} />}
            <span className="mg-edge-label__text">{words}</span>
          </span>
        );
      })}
    </div>
  );
});

const NodeMark = memo(function NodeMark({
  node,
  placed,
  state,
  hovered,
  labelCrowded,
  messages,
  locale,
  onFocusNode,
  onHoverNode,
}: {
  node: GraphNode;
  placed: LayoutResult["nodes"][number];
  state: NodeRenderState;
  hovered: boolean;
  /** No room for this name at the current scale; the stylesheet withholds it. */
  labelCrowded: boolean;
  messages: MemoryGraphMessages;
  locale: string;
  onFocusNode: (nodeId: string | null) => void;
  onHoverNode: (nodeId: string | null) => void;
}) {
  const describedById = `mg-desc-${node.id}`;
  const isBig = node.sizeTier === "hub" || node.sizeTier === "major";
  const statusBadge = node.statusBadge;
  const badge = statusBadge ? badgeText(statusBadge, messages, locale) : null;
  const labelParts = [node.label, messages.entityType[node.entityType]];
  // A retired or merged record must say so in the name a screen reader reads,
  // not only in the ring a sighted reader sees.
  const statusWords = node.status ? messages.entityStatus[node.status] : null;
  if (statusWords) labelParts.push(statusWords);
  if (badge) labelParts.push(badge);

  const style: CSSProperties = {
    ...toneVars(ENTITY_RING_TOKEN[node.entityType]),
    insetInlineStart: `${placed.position.x * 100}%`,
    insetBlockStart: `${placed.position.y * 100}%`,
    inlineSize: `${placed.radius * 2 * 100}%`,
  };

  const className = [
    "mg-node",
    isBig && "mg-node--big",
    node.isNew && "mg-node--isnew",
    state !== "default" && `mg-node--${state}`,
    hovered && "mg-node--neighbor-hot",
    labelCrowded && "mg-node--crowded",
    // An entity no drawn relation reaches. Still on the map, still named, still
    // findable — drawn at a lower weight so the network the reader came for is
    // not buried under the ring of records that are not part of it. See
    // `.mg-node--quiet` in memory-graph.css for what "lower weight" means and
    // for every way it comes back.
    node.linkCount === 0 && "mg-node--quiet",
  ]
    .filter(Boolean)
    .join(" ");

  return (
    <button
      aria-describedby={describedById}
      aria-label={labelParts.join(", ")}
      className={className}
      data-entity-status={node.status ?? undefined}
      data-node-id={node.id}
      data-state={state}
      onClick={() => onFocusNode(node.id)}
      onMouseEnter={() => onHoverNode(node.id)}
      onMouseLeave={() => onHoverNode(null)}
      style={style}
      type="button"
    >
      {/* What a pointer gets from the card below, a screen reader gets here.
          It is a *description*, not part of the name: `aria-label` above wins
          the name computation, so this never lengthens the label the reader
          navigates by — it only answers "what am I looking at" once they land
          on it. */}
      <span className="obsina-sr-only" id={describedById}>
        {[
          node.evidenceCount > 0
            ? messages.peek.evidence(node.evidenceCount)
            : messages.peek.evidenceNone,
          node.linkCount > 0
            ? messages.peek.links(node.linkCount)
            : messages.peek.linksNone,
        ].join(". ")}
        .
      </span>
      {badge && statusBadge && (
        <span className="mg-node__badge" style={toneVars(statusBadge.tone)}>
          <span aria-hidden="true" className="mg-node__badge-dot" />
          {badge}
        </span>
      )}
      <span className="mg-node__circle">
        <span className="mg-node__glyph">
          <Icon icon={ENTITY_ICON_COMPONENT[node.entityType]} size={18} />
        </span>
      </span>
      <span aria-hidden="true" className="mg-node__label">
        {node.label}
      </span>
    </button>
  );
});

function StatusSurface({
  children,
  centered = false,
  announce = false,
}: {
  children: ReactNode;
  centered?: boolean;
  /** Mirrors the design-system `announce` prop: a live region for a screen reader. */
  announce?: boolean;
}) {
  return (
    <div className="mg-status" role={announce ? "status" : undefined}>
      <div
        className={`mg-status__panel${
          centered ? " mg-status__panel--center" : ""
        }`}
      >
        {children}
      </div>
    </div>
  );
}

/**
 * A drawn map that holds nothing.
 *
 * "No graph to draw yet" on its own reads as *we know nothing*, which is a
 * different and much worse claim than the true one: the sources were read, and
 * what they hold is nothing this reader is authorized to see placed. So the
 * surface states how far the reading got and how many required sources are
 * reconciled — the same two facts the unknown surface leads with — and names any
 * gap that is still open. An empty answer is still an answer, and it carries its
 * own watermark.
 */
function EmptySurface({
  asOfText,
  coverage,
  messages,
}: {
  asOfText: string;
  coverage: KnowledgeCoverage;
  messages: MemoryGraphMessages;
}) {
  const reasons = coverage.reasons;
  return (
    <StatusSurface centered>
      <Icon
        className="mg-status__icon"
        icon={Frame}
        label={messages.empty.title}
        size={28}
      />
      <p className="mg-status__title">{messages.empty.title}</p>
      <p className="mg-status__body">{messages.empty.body}</p>
      <p className="mg-status__note">
        {asOfText}
        {" · "}
        {messages.unknown.coverageProgress(
          String(coverage.reconciledSourceCount),
          String(coverage.requiredSourceCount),
        )}
      </p>
      {reasons.length > 0 && (
        <>
          <span className="mg-status__reasons-title">
            {messages.unknown.reasonsTitle}
          </span>
          <ul className="mg-status__reasons">
            {reasons.map((reason) => (
              <li key={reason}>{reasonSentence(reason, messages)}</li>
            ))}
          </ul>
        </>
      )}
    </StatusSurface>
  );
}

/* ------------------------------------------------------------------------- *
 * The hover card: what the thing under the pointer is, in sentences
 * ------------------------------------------------------------------------- */

interface PeekModel {
  readonly title: string;
  /** The kind of thing, or the trust wording for a relation; may be empty. */
  readonly kind: string | null;
  /** What this şuna bağlı:, said as sentences. */
  readonly sentences: readonly string[];
  /** Evidence and reach, plus how far the reading behind them got. */
  readonly facts: readonly string[];
  /** Invitation to the evidence; omitted when there is nothing to open. */
  readonly action: string | null;
}

/**
 * Builds the sentence(s) for whatever the pointer is on.
 *
 * This is the answer to "what is this circle, what is this line, and how do you
 * know" — asked by looking rather than by clicking. It reads only from the drawn
 * graph, so it can neither name an entity the map does not hold nor imply more
 * evidence than the node itself states, and it never claims a watermark it does
 * not have: an unstated `asOf` says so in words rather than being left out.
 */
function describePeek({
  asOf,
  edge,
  layout,
  messages,
  locale,
  node,
  nodeById,
}: {
  asOf: string | null;
  edge: LayoutEdge | null;
  layout: LayoutResult;
  messages: MemoryGraphMessages;
  locale: string;
  node: GraphNode | null;
  nodeById: ReadonlyMap<string, GraphNode>;
}): PeekModel | null {
  // Nothing under the pointer, nothing to say — and nothing to format. The
  // watermark below builds an `Intl.DateTimeFormat`, which is not work to do on
  // every render of a map nobody is pointing at.
  if (!edge && !node) return null;
  const watermark = asOf
    ? messages.peek.asOf(formatInstant(asOf, locale) ?? asOf)
    : messages.peek.asOfUnknown;
  const nameOf = (id: string): string | null => nodeById.get(id)?.label ?? null;

  if (edge) {
    const from = nameOf(edge.sourceNodeId);
    const to = nameOf(edge.targetNodeId);
    // An endpoint the drawn graph cannot name is not guessed at; the line then
    // has no sentence to give and the card stays shut.
    if (from === null || to === null) return null;
    // How far this relation can be trusted rides beside the sentence, in the
    // same words the stroke's pattern already carries. `supported` contributes
    // nothing, so the ordinary case reads as a plain statement.
    const trust = claimStateWords(edge.states, messages).join(" · ");
    return {
      title: relationSentence(edge.relationType, from, to, messages),
      kind: trust === "" ? null : trust,
      sentences: [],
      facts: [messages.peek.evidence(edge.weight), watermark],
      action: null,
    };
  }

  if (!node) return null;

  const touching = layout.edges.filter(
    (candidate) =>
      candidate.sourceNodeId === node.id || candidate.targetNodeId === node.id,
  );
  const ordered = [...touching].sort(
    (left, right) => right.weight - left.weight,
  );
  const sentences: string[] = [];
  for (const candidate of ordered.slice(0, PEEK_MAX_SENTENCES)) {
    const from = nameOf(candidate.sourceNodeId);
    const to = nameOf(candidate.targetNodeId);
    if (from === null || to === null) continue;
    sentences.push(
      relationSentence(candidate.relationType, from, to, messages),
    );
  }
  const remaining = ordered.length - sentences.length;
  if (remaining > 0) sentences.push(messages.peek.more(remaining));

  return {
    title: node.label,
    kind: messages.entityType[node.entityType],
    sentences: sentences.length > 0 ? sentences : [messages.peek.linksNone],
    facts: [
      node.evidenceCount > 0
        ? messages.peek.evidence(node.evidenceCount)
        : messages.peek.evidenceNone,
      watermark,
    ],
    action: node.evidenceCount > 0 ? messages.peek.open : null,
  };
}

/**
 * The card itself: bottom-left of the stage, out of the way of the search bar,
 * the zoom stack and the inspector, and sized by its own sentences. It is a
 * polite live region so a reader on assistive tech is told what the pointer
 * found rather than being left with a silent visual change.
 */
const PeekCard = memo(function PeekCard({ peek }: { peek: PeekModel }) {
  return (
    <div aria-live="polite" className="mg-peek" role="status">
      <p className="mg-peek__title">
        {peek.title}
        {peek.kind && <span className="mg-peek__kind">{peek.kind}</span>}
      </p>
      {peek.sentences.length > 0 && (
        <p className="mg-peek__says">{peek.sentences.join(" · ")}</p>
      )}
      <p className="mg-peek__facts">
        {peek.facts.join(" · ")}
        {peek.action && <span className="mg-peek__action">{peek.action}</span>}
      </p>
    </div>
  );
});

/**
 * The honest unknown surface: never presented as an empty graph. States the
 * coverage watermark, the applicable body and the coverage reasons. Announced,
 * because it replaces a drawn scene a sighted reader sees change; a screen
 * reader would otherwise be left on the previous, resolved reading.
 */
function UnknownSurface({
  data,
  messages,
}: {
  data: UnknownMemoryGraph;
  messages: MemoryGraphMessages;
}) {
  const body =
    data.reason === "no_authorized_evidence"
      ? messages.unknown.evidenceBody
      : messages.unknown.coverageBody[data.coverage.durability ?? "unstated"];
  const reasons = data.coverage.reasons;
  return (
    <StatusSurface announce>
      <p className="mg-status__title">{messages.unknown.title}</p>
      <p className="mg-status__body">{body}</p>
      <p className="mg-status__note">
        {messages.unknown.coverageProgress(
          String(data.coverage.reconciledSourceCount),
          String(data.coverage.requiredSourceCount),
        )}
      </p>
      {reasons.length > 0 && (
        <>
          <span className="mg-status__reasons-title">
            {messages.unknown.reasonsTitle}
          </span>
          <ul className="mg-status__reasons">
            {reasons.map((reason) => (
              <li key={reason}>{reasonSentence(reason, messages)}</li>
            ))}
          </ul>
        </>
      )}
    </StatusSurface>
  );
}
